import {
    createFileRoute,
    Navigate,
    Outlet,
    useRouterState,
} from '@tanstack/react-router';

import { AccountDeactivatedPage } from '@apps/web/components/home/account_deactivated_page';
import { AppLayout } from '@apps/web/components/layout/app_layout';
import { PausedOrganizationBanner } from '@apps/web/components/layout/paused_organization_banner';
import { TrainingCoverageNudge } from '@apps/web/features/training_coverage/ui/training_coverage_nudge';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import {
    isChurnedOrganization,
    isChurnedRestricted,
    isOnboardingRestricted,
    isOnboardingStageOrganization,
    isPausedOrganization,
} from '@apps/web/lib/customer_lifecycle';
import { modeStore } from '@apps/web/lib/mode_store';
import { useUser } from '@apps/web/swr_data_fetching/current_user_queries';
import { useOrganization } from '@apps/web/swr_data_fetching/organization_queries';

export const Route = createFileRoute('/_app/(main)')({
    beforeLoad: modeStore.activate,
    onLeave: modeStore.deactivate,
    component: MainAppLayout,
});

function MainAppLayout() {
    const organizationId = useOrganizationId();
    const currentPath = useRouterState({
        select: (state) => state.location.pathname,
    });
    const {
        data: organization,
        error: organizationError,
        isLoading: isLoadingOrganization,
    } = useOrganization(organizationId, {
        reason: 'pre-onboarding-route-gate',
    });
    const { data: user, isLoading: isLoadingUser } = useUser({
        reason: 'pre-onboarding-route-gate',
    });
    const allowedOnboardingPath =
        currentPath === '/' ||
        currentPath === '/home' ||
        currentPath === '/settings' ||
        currentPath.startsWith('/settings/') ||
        currentPath === '/routines' ||
        currentPath.startsWith('/routines/') ||
        currentPath === '/automations' ||
        currentPath.startsWith('/extension-review') ||
        currentPath.startsWith('/admin');
    // On routes the constrained onboarding workspace hides, wait until we
    // know both the org lifecycle and whether the user is a platform admin
    // before rendering or redirecting. Without waiting for the user, an admin
    // hitting a blocked route on a cold load would be redirected to /home
    // before we learn they should not be gated.
    if (
        organizationId &&
        !allowedOnboardingPath &&
        (isLoadingOrganization ||
            organizationError ||
            !organization ||
            (isOnboardingStageOrganization(organization) && isLoadingUser))
    ) {
        return null;
    }

    if (organization && isChurnedOrganization(organization) && isLoadingUser) {
        return null;
    }

    if (organization && isChurnedRestricted(organization, user?.admin)) {
        return (
            <AppLayout>
                <AccountDeactivatedPage organization={organization} />
            </AppLayout>
        );
    }

    if (
        isOnboardingRestricted(organization, user?.admin) &&
        !allowedOnboardingPath
    ) {
        return <Navigate to="/home" replace={true} />;
    }

    return (
        <AppLayout>
            {isPausedOrganization(organization) && <PausedOrganizationBanner />}
            <TrainingCoverageNudge />
            <Outlet />
        </AppLayout>
    );
}
